import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { FaUpload, FaTimes, FaImage } from "react-icons/fa";

export const AddCollege = () => {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    logo: null,
    images: [],
    tag: "private",
    description: "",
    categories: [],
    courses: [],
    admissionFee: "",
    isShowTop: false,
    isHide: false
  });
  
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  const managers = [
    { name: "Employee Manager", path: "/employee-manager" },
    { name: "Student Manager", path: "/student-manager" },
    { name: "Cold leads dump", path: "/nl-nc-student-manager" },
    { name: "College Manager", path: "/college-manager" },
    { name: "Course Manager", path: "/course-manager" },
    { name: "Admission Manager", path: "/admission-manager" }
  ];

  // Tag options
  const tagOptions = [
    { value: 'private', label: 'Private' },
    { value: 'government', label: 'Government' }
  ];

  // Fetch categories and courses
  useEffect(() => {
    fetchCategories();
    fetchCourses();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/category/all?limit=100`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${localStorage.getItem("token")}`,
        },
      });
      const categoryOptions = response.data.data?.map(cat => ({
        value: cat._id,
        label: cat.name
      })) || [];
      setCategories(categoryOptions);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/get-all-courses`, {
        headers: { "Content-Type": "application/json",
          Authorization: `${localStorage.getItem("token")}` },
      });
      const courseOptions = response.data.users?.map(course => ({
        value: course._id,
        label: course.name
      })) || [];
      setCourses(courseOptions);
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  // Handle file upload
  const handleFileUpload = async (files, type) => {
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/upload/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `${localStorage.getItem("token")}`,
        },
      });

      const urls = response.data.urls;
      
      if (type === 'logo' && urls.length > 0) {
        setFormData(prev => ({ ...prev, logo: urls[0] }));
        setLogoPreview(urls[0]);
      } else if (type === 'images') {
        setFormData(prev => ({ 
          ...prev, 
          images: [...prev.images, ...urls].slice(0, 5) // Max 5 images
        }));
        setImagePreviews(prev => [...prev, ...urls].slice(0, 5));
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setUploading(false);
    }
  };

  // Handle logo change
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload([file], 'logo');
    }
  };

  // Handle images change
  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 5 - formData.images.length;
    const filesToUpload = files.slice(0, remainingSlots);
    
    if (filesToUpload.length > 0) {
      handleFileUpload(filesToUpload, 'images');
    }
  };

  // Remove image
  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Remove logo
  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo: null }));
    setLogoPreview(null);
  };

  // Validation function
  const validateForm = () => {
    let errors = {};
    if (!formData.name.trim()) {
      errors.name = "College name is required";
    }
    if (!formData.address.trim()) {
      errors.address = "College address is required";
    }
    if (!String(formData.admissionFee).trim()) {
      errors.admissionFee = "Admission fee is required";
    } else if (isNaN(formData.admissionFee) || parseFloat(formData.admissionFee) < 0) {
      errors.admissionFee = "Please enter a valid amount";
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData = {
        ...formData,
        categories: formData.categories.map(cat => cat.value),
        courses: formData.courses.map(course => course.value)
      };

      await axios.post(
        location?.state?.type !== "edit" 
          ? `${process.env.REACT_APP_BASE_URL}/create-college` 
          : `${process.env.REACT_APP_BASE_URL}/edit-college`,
        {
          ...submitData,
          _id: location?.state?.data?._id
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );
      
      // Reset form
      setFormData({
        name: "",
        address: "",
        logo: null,
        images: [],
        tag: "private",
        description: "",
        categories: [],
        courses: [],
        admissionFee: "",
        isShowTop: false,
        isHide: false
      });
      setLogoPreview(null);
      setImagePreviews([]);
      setErrors({});
      navigate("/college-manager");
    } catch (error) {
      console.error("Error adding college:", error);
    }
  };

  // Initialize form data for edit/view
  useEffect(() => {
    if ((location.pathname === "/view-college" || location.pathname === "/edit-college") && !location.state) {
      navigate("/college-manager");
      return;
    }
    
    if (location?.state?.type === "view" || location?.state?.type === "edit") {
      const data = location?.state?.data;
      console.log(data)
      setFormData({
        name: data?.name || "",
        address: data?.address || "",
        logo: data?.logo || null,
        images: data?.images || [],
        tag: data?.tag || "private",
        description: data?.description || "",
        categories: [],
        courses: [],
        admissionFee: data?.admissionFee || "",
        isShowTop: data?.isShowTop || false,
        isHide: data?.isHide || false
      });
      setLogoPreview(data?.logo || null);
      setImagePreviews(data?.images || []);
    }
  }, [location]);

  // Update categories and courses when they are loaded
  useEffect(() => {
    if ((location?.state?.type === "view" || location?.state?.type === "edit") && location?.state?.data) {
      const data = location?.state?.data;
      
      // Update categories
      if (categories.length > 0 && data?.categories) {
        const selectedCategories = data.categories.map(cat => {
          // Handle both ID and full object cases
          const catId = typeof cat === 'object' ? cat._id : cat;
          return categories.find(option => option.value === catId);
        }).filter(Boolean);
        setFormData(prev => ({ ...prev, categories: selectedCategories }));
      }
      
      // Update courses
      if (courses.length > 0 && data?.courses) {
        const selectedCourses = data.courses.map(course => {
          // Handle both ID and full object cases
          const courseId = typeof course === 'object' ? course._id : course;
          return courses.find(option => option.value === courseId);
        }).filter(Boolean);
        console.log(selectedCourses)
        setFormData(prev => ({ ...prev, courses: selectedCourses }));
      }
    }
  }, [location, categories, courses]);

  const isViewMode = location?.state?.type === "view";

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-black">
            {isViewMode ? "View" : location?.state?.type === "edit" ? "Edit" : "Add"} College
          </h1>
        </div>

        {/* Add College Form */}
        <div className="bg-white p-6 shadow-md rounded-lg">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* College Name */}
              <div>
                <label htmlFor="name" className="block text-gray-700 font-bold mb-2">
                  College Name *
                </label>
                <input
                  type="text"
                  id="name"
                  disabled={isViewMode}
                  value={formData.name}
                  onChange={(e) => {
                    setErrors(prev => ({ ...prev, name: "" }));
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                  }}
                  placeholder="Enter college name"
                  className="w-full border border-gray-300 rounded-md px-4 py-2"
                />
                {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
              </div>

              {/* College Address */}
              <div>
                <label htmlFor="address" className="block text-gray-700 font-bold mb-2">
                  College Address *
                </label>
                <input
                  type="text"
                  id="address"
                  disabled={isViewMode}
                  value={formData.address}
                  onChange={(e) => {
                    setErrors(prev => ({ ...prev, address: "" }));
                    setFormData(prev => ({ ...prev, address: e.target.value }));
                  }}
                  placeholder="Enter college address"
                  className="w-full border border-gray-300 rounded-md px-4 py-2"
                />
                {errors.address && <p className="text-red-500 text-sm">{errors.address}</p>}
              </div>

              {/* Admission Fee */}
              <div>
                <label htmlFor="admissionFee" className="block text-gray-700 font-bold mb-2">
                  Admission Fee *
                </label>
                <input
                  type="text"
                  id="admissionFee"
                  disabled={isViewMode}
                  value={formData.admissionFee}
                  onChange={(e) => {
                    setErrors(prev => ({ ...prev, admissionFee: "" }));
                    setFormData(prev => ({ ...prev, admissionFee: e.target.value }));
                  }}
                  placeholder="Enter admission fee amount"
                  className="w-full border border-gray-300 rounded-md px-4 py-2"
                />
                {errors.admissionFee && <p className="text-red-500 text-sm">{errors.admissionFee}</p>}
              </div>

              {/* Tag */}
              <div>
                <label className="block text-gray-700 font-bold mb-2">
                  College Type
                </label>
                <Select
                  value={tagOptions.find(option => option.value === formData.tag)}
                  onChange={(selected) => setFormData(prev => ({ ...prev, tag: selected.value }))}
                  options={tagOptions}
                  isDisabled={isViewMode}
                  className="w-full"
                />
              </div>

              {/* Categories */}
              <div>
                <label className="block text-gray-700 font-bold mb-2">
                  Categories
                </label>
                <Select
                  value={formData.categories}
                  onChange={(selected) => setFormData(prev => ({ ...prev, categories: selected }))}
                  options={categories}
                  isMulti
                  isDisabled={isViewMode}
                  className="w-full"
                  placeholder="Select categories"
                />
              </div>

              {/* Courses */}
              <div>
                <label className="block text-gray-700 font-bold mb-2">
                  Courses
                </label>
                <Select
                  value={formData.courses}
                  onChange={(selected) => setFormData(prev => ({ ...prev, courses: selected }))}
                  options={courses}
                  isMulti
                  isDisabled={isViewMode}
                  className="w-full"
                  placeholder="Select courses"
                />
              </div>
            </div>

            {/* Description */}
            <div className="mt-6">
              <label htmlFor="description" className="block text-gray-700 font-bold mb-2">
                Description
              </label>
              <textarea
                id="description"
                disabled={isViewMode}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter college description"
                rows="4"
                className="w-full border border-gray-300 rounded-md px-4 py-2"
              />
            </div>

            {/* Logo Upload */}
            <div className="mt-6">
              <label className="block text-gray-700 font-bold mb-2">
                College Logo
              </label>
              <div className="flex items-center space-x-4">
                {!isViewMode && (
                  <label className="flex items-center gap-2 bg-blue text-white px-4 py-2 rounded-md cursor-pointer hover:bg-blue-dark">
                    <FaUpload />
                    <span>Upload Logo</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </label>
                )}
                {logoPreview && (
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="h-20 w-20 object-cover rounded-md"
                    />
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <FaTimes size={12} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Images Upload */}
            <div className="mt-6">
              <label className="block text-gray-700 font-bold mb-2">
                College Images (Max 5)
              </label>
              <div className="space-y-4">
                {!isViewMode && (
                  <label className="flex items-center gap-2 bg-blue text-white px-4 py-2 rounded-md cursor-pointer hover:bg-blue-dark">
                    <FaUpload />
                    <span>Upload Images</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImagesChange}
                      className="hidden"
                      disabled={formData.images.length >= 5}
                    />
                  </label>
                )}
                
                {/* Image Previews */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {imagePreviews.map((image, index) => (
                    <div key={index} className="relative">
                      <img
                        src={image}
                        alt={`College image ${index + 1}`}
                        className="h-24 w-full object-cover rounded-md"
                      />
                      {!isViewMode && (
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <FaTimes size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                {formData.images.length >= 5 && !isViewMode && (
                  <p className="text-gray-500 text-sm">Maximum 5 images reached</p>
                )}
              </div>
            </div>

            {/* Checkboxes */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Show Top */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isShowTop}
                    onChange={(e) => setFormData(prev => ({ ...prev, isShowTop: e.target.checked }))}
                    disabled={isViewMode}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700 font-medium">Show Top</span>
                </label>
              </div>

              {/* Hide */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isHide}
                    onChange={(e) => setFormData(prev => ({ ...prev, isHide: e.target.checked }))}
                    disabled={isViewMode}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700 font-medium">Hide</span>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            {!isViewMode && (
              <div className="mt-6">
                <button
                  type="submit"
                  disabled={uploading}
                  className="bg-blue text-white px-6 py-2 rounded-md shadow-md hover:bg-blue-dark disabled:opacity-50"
                >
                  {uploading ? "Uploading..." : "Submit"}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCollege;
